import Vue from 'vue';
import { mapGetters } from 'vuex';
import { guid } from '@/mixins/guid';
import { modals } from '@/mixins/modal';
import { uploader } from '@/mixins/aws/uploader/s3';
import uploadProgress from '@/mixins/aws/uploader';
import path from 'path';
import _ from 'lodash';

export default Vue.component('edit-audio-series', {
  template: require('./template.html'),
  mixins: [guid, modals, uploader],
  components: {
    uploadProgress,
  },
  computed: {
    ...mapGetters(['viewport', 'currentSite']),
    episodes() {
      return this.droplet.episodes;
    },
  },
  data() {
    return {
      paginate: ['episodes'],
      activeEpisode: null,
      activeDelete: null,
      showAllEpisodes: false,
      customErrors: false,
      itemLimit: 4,
      loading: false,
      prefix: 'drz_audio_episode_',
      bucketPath: '_droplet-assets/audioSeries/',
      s3Url: process.env.S3_ASSETS_URL,
      audioFiles: [],
      confirmModal: false,
      showFiles: false,
    };
  },
  name: 'edit-audio-series',
  props: ['droplet', 'show-edit-bubble'],
  methods: {
    setEpisodeTitle(e, episode) {
      this._debounce(() => {
        const text = e.target.value;
        const val = text.trim() !== '' ? text : false;
        this.$store.dispatch('setDropletVal', {
          drp: episode,
          key: 'title',
          val,
        });
      }, 250);
    },
    openUpload() {
      this.$refs.uploadFile.click();
    },
    adjustGridCss() {
      // for ie / edge we need to create grid css on the fly based on how many episodes
      // in the grid, so we sweep and add / remove as needed.
      const itemSelector = '.drzAudio-episodes-container > .drzAudio-episodes-item:nth-child';
      const itemReg = new RegExp(`^${itemSelector}`, 'gi');
      for (let i = this.droplet.mediaQueries.length - 1; i >= 0; --i) {
        const item = this.droplet.mediaQueries[i];
        if (item.selector.match(itemReg)) {
          this.$store.dispatch('spliceDropletVal', {
            index: i,
            array: this.droplet.mediaQueries,
          });
        }
      }

      _.forEach(this.droplet.episodes, (episode, i) => {
        const desktopRow = Math.floor(i / 3) + 1;
        const desktopCol = (i % 3) + 1;
        const mobileRow = (i + 1);
        const desktop = `${desktopRow} / ${desktopCol} / ${desktopRow + 1} / ${desktopCol + 1}`;
        const mobile = `${mobileRow} / 1 / ${mobileRow + 1} / 4`;
        this.$store.dispatch('pushDropletVal', {
          array: this.droplet.mediaQueries,
          val: {
            selector: `${itemSelector}(${i + 1})`,
            properties: [
              {
                property: 'grid-area',
                desktop,
                tablet: 'default',
                mobile,
              },
            ],
          },
        });
      });
      this.$emit('css-generator');
    },
    async uploadAudio(e) {
      this.customErrors = false;
      this.$validator.validateAll().then(async (result) => {
        // run only if file types and dimensions are correct
        if (result) {
          this.loading = true;
          const newFiles = [...e.target.files];
          const episodes = this.droplet.episodes.map(e => path.basename(e.source).replace(`${this.prefix}`, ''));
          const alreadyExists = newFiles.map(f => f.name).some(r => episodes.includes(r));
          if (newFiles.length > 0 && !alreadyExists) {
            try {
              // check for expired sessions first
              await this._cognitoCheckSession();
              await Promise.all(newFiles.map(async (f) => {
                let fileName = path.basename(f.name);
                fileName = `${this.prefix}${_.trim(fileName)}`;
                fileName = _.replace(fileName, /\s+/g, '_');
                const filePath = `/${this.bucketPath}${fileName}`;
                await this.uploadFile({
                  file: `${this._sitePath}${filePath}`,
                  body: f,
                });
                const published = this.getMonthDay(new Date().toISOString());
                this.$store.dispatch('prependDropletVal', {
                  array: this.droplet.episodes,
                  val: {
                    _id: this.guid(),
                    title: 'Sermon Title - Message Title',
                    source: filePath,
                    published,
                  },
                });
                return new Promise(resolve => resolve());
              }));
              this.adjustGridCss();
            } catch (err) {
              if (!err.message.match(/aborted/gi)) {
                this.$log.error(err);
                await this._reportError({
                  error: err,
                  fn: 'uploadAudio - series',
                });
              }
            }
          }
          if (alreadyExists) {
            this.customErrors = '1 or more of the files you would like to upload is already in the series list';
          }
          this.loading = false;
          // reset value to null so we can get the "change" event to run
          // in any case. Even when the user selects the same file
          e.target.value = null;
        }
      });
    },
    toggleModal() {
      this.confirmModal = !this.confirmModal;
      const content = this.$refs.confirmModal;
      if (this.confirmModal) {
        this.modelMoveOut(content);
      } else {
        this.modalMoveIn(content);
      }
    },
    confirmDelete(file) {
      this.activeDelete = file;
      this.toggleModal();
    },
    async deleteAudioFile() {
      // prep notifcation type
      const notif = {
        id: this.guid(),
      };
      const file = path.basename(this.activeDelete.source);
      try {
        this.loading = true;
        // delete original
        await this._s3deleteFile({
          key: `${this._fullSiteFolder}${this.activeDelete.source}`,
        });
        const i = _.findIndex(this.droplet.episodes, { _id: this.activeDelete._id });
        this.$store.dispatch('spliceDropletVal', {
          array: this.droplet.episodes,
          index: i,
        });
        this.adjustGridCss();
        // set for notifications
        notif.type = 'success';
        notif.message = `Successfully deleted audio file: ${file}`;
      } catch (err) {
        // set for notifications
        notif.type = 'error';
        notif.message = `${err.message || err}. This has been reported.`;
        await this._reportError({
          error: err,
          fn: 'deleteAudioFile',
        });
      } finally {
        this.$store.dispatch('addNotification', notif);
        this.loading = false;
      }
    },
    getFileName(string) {
      return path.basename(string);
    },
    drzRevertData() {
      this.adjustGridCss();
    },
    formatErrors(ers) {
      return ers.join(' ');
    },
  },
});



// WEBPACK FOOTER //
// ./src/global-view/editor/droplets/audio-series/components/_edit-options/index.js